import { useCallback, useEffect, useRef, useState } from "react";
import { mailApi } from "../mailApi";
import { analysisKey } from "./assistantLabels";

const POLL_INTERVAL = 3000;
// The server analyses at most this many messages per request.
export const MAX_BATCH = 25;

const isBusy = analysis =>
  analysis?.status === "processing" || analysis?.replyStatus === "processing";

const toRef = message => ({ uid: message.uid, messageId: message.messageId || null });

/**
 * State of the mail assistant for one mailbox and folder: whether it is set up, the
 * analyses of the listed messages, and polling while the server works on some of them.
 */
export const useMailAssistant = ({ accountId, folder, messages, language }) => {
  const [status, setStatus] = useState(null);
  const [analyses, setAnalyses] = useState({});
  const [directory, setDirectory] = useState(null);
  // Messages the server is working on, which may not be on the current page any more.
  const pending = useRef(new Map());

  const merge = useCallback(
    list =>
      setAnalyses(prev => {
        const next = { ...prev };
        (list || []).forEach(analysis => {
          const key = analysis.key;
          next[key] = analysis;
          if (isBusy(analysis)) {
            pending.current.set(key, { uid: analysis.uid, messageId: analysis.messageId });
          } else {
            pending.current.delete(key);
          }
        });
        return next;
      }),
    []
  );

  useEffect(() => {
    setStatus(null);
    setAnalyses({});
    pending.current.clear();
    if (!accountId) return;
    mailApi
      .getAssistant(accountId)
      .then(res => setStatus(res.data))
      .catch(() => setStatus({ configured: false, consentAt: null }));
  }, [accountId]);

  // Analyses of the messages on the current page.
  useEffect(() => {
    if (!accountId || !status?.consentAt || !messages.length) return;
    mailApi
      .getAnalyses(accountId, { folder, messages: messages.map(toRef) })
      .then(res => merge(res.data))
      .catch(() => {});
  }, [accountId, folder, messages, status?.consentAt, merge]);

  // Poll while the server is still working on something.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!accountId || !pending.current.size) return undefined;
    const timer = setTimeout(async () => {
      try {
        const res = await mailApi.getAnalyses(accountId, {
          folder,
          messages: Array.from(pending.current.values())
        });
        merge(res.data);
      } catch {
        // Try again on the next tick.
      }
      setTick(value => value + 1);
    }, POLL_INTERVAL);
    return () => clearTimeout(timer);
  }, [accountId, folder, analyses, tick, merge]);

  const analysisOf = message => (message ? analyses[analysisKey(folder, message)] : null);

  const analyze = async (list, { force = false } = {}) => {
    const res = await mailApi.analyze(accountId, {
      folder,
      messages: list.slice(0, MAX_BATCH).map(toRef),
      force,
      language
    });
    merge(res.data);
    return res.queued;
  };

  const redraftReply = async (message, instructions) => {
    const res = await mailApi.redraftReply(accountId, {
      folder,
      ...toRef(message),
      instructions,
      language
    });
    merge([res.data]);
  };

  const saveAttachments = async (message, items) => {
    const res = await mailApi.saveAttachments(accountId, { folder, ...toRef(message), items });
    const refreshed = await mailApi.getAnalyses(accountId, { folder, messages: [toRef(message)] });
    merge(refreshed.data);
    return res;
  };

  const loadDirectory = useCallback(async () => {
    const res = await mailApi.getDirectory();
    setDirectory(res.data || []);
    return res.data;
  }, []);

  const setConsent = async enabled => {
    const res = await mailApi.setAssistantConsent(accountId, enabled);
    setStatus(prev => ({ ...prev, consentAt: res.data.consentAt }));
  };

  return {
    status,
    enabled: Boolean(status?.configured && status?.consentAt),
    analyses,
    analysisOf,
    analyze,
    redraftReply,
    saveAttachments,
    directory,
    loadDirectory,
    setConsent
  };
};
